// @givmo/board — every word the committee screens say (CBG-YR6-REG, CBG-YR6-AUTH; frames c1, c1b,
// c2, c3, c6 of design-155, and the dialogs no frame drew).
//
// Every line was approved in the copy round CBG-YR6-COPY1 (Jonathan, 2026-10-15 10:32 PDT), but for
// the two "Serves from" notes (CBG-FIX85), approved with cut scho's words in CBG-YR5-COPY1's next
// part (2026-10-16 19:35 PDT). The lines marked APPROVED are reused word for word from shipped
// screens and say where from.
//
// ⚠ Plan §3.2 and §3.4 bind every line. Board never says whether a committee has authority to act
// for the board, never says what an answer means for the Form 990, and never says "must", "should",
// "required", "advisory" or "authorized" in its own voice. The IRS's words are quoted as the IRS's,
// with where they are written. A person is named in full; no pronoun for a named person.
//
// A committee's meetings, its next meeting and "From your board's way" are CBG-YR6-MEET's and
// -APPROVE's words (`committee-meetings-words.ts`); its decisions for the board are CBG-YR6-CONFIRM's.

export const committeesWords = {
  // ── The Committees page (frames c1, c1b) ───────────────────────────────────────────────────────
  pageTitle: 'Committees',
  // The page's description is CBG-YR6-MEET's, its meetings back in it (`committee-meetings-words.ts`).
  addCommittee: 'Add a committee',
  columnCommittee: 'Committee',
  columnChair: 'Chair',
  columnActsForBoard: 'Acts for the board',
  /** The column's fine print: whose answer it is, and where the question comes from. */
  actsForBoardHow: [
    'Your organization’s own answer, for each committee, to the question Form 990, Part VI, line 8b asks: whether it has authority to act on behalf of the board.',
    'Givmo records the answer and where it’s written. It doesn’t read your governing documents to decide it.',
  ],
  continuity: (value: string) => (value === 'ad_hoc' ? 'Ad hoc' : 'Standing'),
  answerShort: (choice: string | null | undefined) =>
    choice === 'broad' || choice === 'particular_matters' ? 'Yes' : choice === 'none' ? 'No' : '—',
  dissolvedLine: (names: string) => `Dissolved: ${names}`,
  loadFailed: 'We couldn’t load this board’s committees.',
  /** GCF's case (c1b): no committee recorded. The IRS's own sentence, quoted, and nothing else. */
  emptyTitle: 'No committees recorded',
  emptyLine: 'Add each committee your board has, with its members and its charter.',
  emptyInstructionQuote: '“If the organization had no committees, answer ‘No’ on line 8b.”',
  emptyInstructionSource: 'Instructions for Form 990 (2025), Part VI, line 8',

  // ── Adding a committee (frame c3) ───────────────────────────────────────────────────────────────
  addTitle: 'Add a committee',
  nameLabel: 'Name',
  kindLabel: 'Kind',
  standing: 'Standing',
  standingNote: 'Ongoing work, like finance or governance.',
  adHoc: 'Ad hoc',
  adHocNote: 'One task, ended when the board dissolves it.',
  formLabel: 'Start its charter from',
  noCharterYet: 'No charter yet',
  formClosing:
    'Adding it opens a draft of its charter from the form, for your board to fill in and adopt.',
  establishedLabel: 'Established on',
  recordedInLabel: 'Where it’s recorded (optional)',
  recordedInNone: 'Not recorded',
  /** A link this reader can't list (a closed session's motion they weren't in): kept, unnamed. */
  recordedInHidden: 'A record you can’t open here',
  addSave: 'Add the committee',
  added: (name: string) => `${name} added.`,
  addFailed: 'We couldn’t add that committee. Please try again.',

  // ── One committee's page (frame c2, without its meetings) ───────────────────────────────────────
  headerLine: (continuity: string, established: string | null) =>
    established === null
      ? `${continuity === 'ad_hoc' ? 'Ad hoc' : 'Standing'} committee`
      : `${continuity === 'ad_hoc' ? 'Ad hoc' : 'Standing'} committee · Established ${established}`,
  dissolvedHeader: (day: string) => `Dissolved ${day}`,
  allCommittees: 'All committees',
  notFound: 'We couldn’t find that committee.',
  moreActions: 'More for this committee',
  membersTitle: 'Members',
  addMember: 'Add a member',
  noMembers: 'No members recorded.',
  roleChair: 'Chair',
  director: 'Director',
  notADirector: 'Not a director',
  doesNotVote: 'Doesn’t vote on the committee',
  noLongerServing: 'No longer on the board',
  since: (day: string) => `Since ${day}`,
  until: (day: string) => `Until ${day}`,
  endedOn: (day: string) => `Ended ${day}`,
  formerMembers: 'Earlier members',
  endMembership: 'End membership',
  charterTitle: 'Charter',
  charterVersion: (day: string) => `Adopted ${day}`,
  charterDraftOnly: 'A draft is in your minute book; it becomes the charter once approved.',
  addVersion: 'Add a version',
  noCharter: 'No charter recorded.',
  startFromForm: 'Start from a form',
  importCharter: 'Import one adopted before Givmo',
  linkCharter: 'Link a charter already recorded',
  rulesTitle: 'Its rules',
  noticeLabel: 'Notice period',
  quorumLabel: 'Quorum',
  minutesWayLabel: 'Its minutes are approved by',
  /** APPROVED (Meeting rules, `parameters-page.tsx`). */
  notCurrentlyRecorded: 'Not currently recorded',
  addRule: 'Add a rule',

  // ── The actions menu, and its dialogs (no frame drew these) ──────────────────────────────────────
  changeDetails: 'Change its details',
  changeTitle: (name: string) => `Change ${name}`,
  saveChanges: 'Save changes',
  changed: 'Changes saved.',
  changeFailed: 'We couldn’t save those changes. Please try again.',
  linkTitle: 'Link a charter already recorded',
  linkLabel: 'Which charter',
  linkNote:
    'The committee charters in your governing documents that no other committee holds. Board reads no charter to decide anything.',
  linkNone: 'Every committee charter you’ve recorded is already held by a committee.',
  linkSave: 'Link it',
  linked: 'Charter linked.',
  linkFailed: 'We couldn’t link that charter. Please try again.',
  dissolve: 'Dissolve the committee',
  dissolveTitle: (name: string) => `Dissolve ${name}`,
  dissolveBody:
    'Every membership ends with it. Its members, its charter, its rules and its answer stay in your records.',
  dissolvedOnLabel: 'Dissolved on',
  noteLabel: 'Note (optional)',
  dissolveSave: 'Dissolve it',
  dissolved: (name: string) => `${name} dissolved.`,
  dissolveFailed: 'We couldn’t dissolve that committee. Please try again.',
  correct: 'Correct the dissolution',
  correctTitle: (name: string) => `Correct ${name}’s dissolution`,
  correctBody:
    'The committee stands again, and the memberships its dissolution ended stand again for each member still on the board.',
  correctNoteLabel: 'Why it’s corrected',
  correctNoteNote: 'Kept in the committee’s history.',
  correctSave: 'Correct it',
  corrected: 'Dissolution corrected.',
  correctFailed: 'We couldn’t correct that dissolution. Please try again.',

  // ── A member's dialogs (no frame drew these) ─────────────────────────────────────────────────────
  addMemberTitle: (committee: string) => `Add a member to ${committee}`,
  changeMemberTitle: (name: string) => `Change ${name}’s membership`,
  seatLabel: 'Who',
  noSeats: 'Everyone serving on your board is already on this committee.',
  roleLabel: 'Role',
  roleMember: 'Member',
  votesLabel: 'Votes on the committee',
  votesNote:
    'As your organization records it. Givmo doesn’t decide it from whether someone is a director.',
  servesFromLabel: 'Serves from (optional)',
  servesToLabel: 'Serves until (optional)',
  // Not drawn (CBG-FIX85): under "Serves from" in "Add a member". It states how the minutes read a
  // membership (`board-committee-meetings.md` §7.1): from "Serves from", else from the day it's
  // recorded, in the role and vote recorded when it was added.
  servesFromNoteAdd:
    'Their committee’s minutes show them as a member, in the role chosen here, from the “Serves from” day. Left empty, that’s the day you add them, so a meeting before then shows them as a guest.',
  // Not drawn (CBG-FIX85): under "Serves from" in "Change". A change of role or vote counts from
  // the day it's saved (§7.1), so this one never says "in the role chosen here".
  servesFromNoteChange:
    'Their committee’s minutes show them as a member from the “Serves from” day. Left empty, that’s the day they were added, so a meeting before then shows them as a guest. A change of role or vote counts from the day you save it.',
  memberSave: 'Add them',
  memberChangeSave: 'Save changes',
  memberAdded: (name: string) => `${name} added.`,
  memberChanged: 'Membership saved.',
  memberFailed: 'We couldn’t save that membership. Please try again.',
  endTitle: (name: string) => `End ${name}’s membership`,
  endBody: 'Their membership stays in the committee’s history.',
  endLastDayLabel: 'Last day (optional)',
  endSave: 'End it',
  ended: 'Membership ended.',
  endFailed: 'We couldn’t end that membership. Please try again.',
  datesOutOfOrder: 'An end can’t be before its start.',

  // ── Line 8b's answer (frames c2 and c6) ─────────────────────────────────────────────────────────
  authorityTitle: 'Authority to act for the board',
  authoritySource: 'Your organization’s answer to Form 990, Part VI, line 8b',
  answerLabel: (choice: string | null | undefined) =>
    choice === 'broad'
      ? 'Yes, broad authority'
      : choice === 'particular_matters'
        ? 'Yes, limited to particular matters'
        : 'No',
  /** APPROVED (Meeting rules, `parameters-page.tsx`), the citation's own line. */
  fromYourDocuments: (citation: string) => `From your governing documents: ${citation}`,
  recordedOn: (day: string) => `Recorded ${day}`,
  notAnswered: 'Not answered',
  recordAnswer: 'Record your organization’s answer',
  changeAnswer: 'Record a new answer',
  dialogTitle: (committee: string) => `${committee}: authority to act for the board`,
  questionLead:
    'Form 990, Part VI, line 8b asks about each committee with authority to act on behalf of the governing body.',
  whatInstructionsSay: 'What the instructions say',
  instructionLines: [
    '“…committees with authority to act on behalf of the governing body (which ordinarily don’t include advisory boards).” — Instructions for Form 990 (2025), Part VI, line 8',
    '“The organization need not describe on Schedule O (Form 990) delegations of authority that are limited in scope to particular areas or matters, such as delegations to an audit committee, investment committee, or compensation committee of the governing body.” — Part VI, line 1a',
  ],
  attachedMembers: 'Its members',
  attachedCharter: 'Its charter',
  answerGroupLabel: 'Your organization’s answer',
  /** APPROVED (Meeting rules' dialog, `record-parameter-dialog.tsx`): the citation field and its note. */
  citationLabel: 'Where this comes from',
  citationNote:
    'The section of your own governing documents, e.g. “Art. III §4”. Required — a rule without it isn’t attributable to anything.',
  /** APPROVED (`parameters-page.tsx`), then one line approved in CBG-YR6-COPY1. */
  doesntRead: 'Givmo doesn’t read your governing documents.',
  recordsYours: 'This records your organization’s answer and where it’s written.',
  /** APPROVED (Meeting rules' dialog). */
  replacesLine:
    'Recording a new rule replaces the one in force and keeps the old one in your history — nothing already recorded changes.',
  recordTheAnswer: 'Record the answer',
  answerRecorded: 'Answer recorded.',
  answerFailed: 'We couldn’t record that answer. Please try again.',
  cancel: 'Cancel',

  // ── A committee's rules (CBG-YR6-AUTH, the "Its rules" card's dialog) ────────────────────────────
  /** APPROVED (Meeting rules' dialog): its title, its fields and its buttons, word for word, except
   *  "A number of members", "A fraction of members" and "Number of members", adapted from its
   *  "…seats" and approved with this page's words (CBG-YR6-COPY1, 2026-10-15 10:32 PDT). */
  ruleDialogTitle: 'Add a rule',
  whichRule: 'Which rule',
  daysOfNotice: 'Days of notice',
  daysNote: 'Whole days, as your governing documents state them.',
  statedAs: 'Stated as',
  aNumberOfMembers: 'A number of members',
  aFractionOfMembers: 'A fraction of members',
  numberOfMembers: 'Number of members',
  thisMany: 'This many',
  outOf: 'Out of',
  recordIt: 'Record it',
  ruleRecorded: 'Rule recorded.',
  ruleFailed: 'We couldn’t record that rule. Please try again.',
  /** A committee's three ways for its minutes; "An officer alone" is APPROVED (CBG-RC11-COPY1). */
  wayLabel: (way: string) =>
    way === 'written_consent'
      ? 'Written consent of its members'
      : way === 'next_meeting'
        ? 'A vote at its next meeting'
        : 'An officer alone',
  wayDescription: (way: string) =>
    way === 'written_consent'
      ? 'Its members sign the minutes’ approval.'
      : way === 'next_meeting'
        ? 'Its members approve them at the committee’s next meeting.'
        : 'An officer approves them on the committee’s behalf.',
} as const;
